const COMPACT_TOP_VALUE_LIMIT = 10;
const EXPANDED_TOP_VALUE_LIMIT = 50;

function toNumber(value, fallback = 0) {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (value == null) return fallback;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function roundPercent(count, total) {
    return total > 0 ? parseFloat(((count / total) * 100).toFixed(1)) : 0;
}

function getBucketType(value) {
    const lower = String(value || "").toLowerCase();
    if (lower === "null" || lower === "(null)") return "null";
    if (lower === "blank" || lower === "(blank)") return "blank";
    if (lower === "others" || lower === "other" || lower === "others (approx.)") return "others";
    return "value";
}

function isValueBucket(entry) {
    return entry && entry.bucketType === "value";
}

function hasBucket(entries, bucketType) {
    return entries.some((entry) => entry.bucketType === bucketType);
}

function normalizeDistributionEntries(stats, sourceEntries = []) {
    const total = toNumber(stats && stats.totalRecords);

    return (sourceEntries || []).filter(Boolean).map((entry, index) => {
        const count = toNumber(entry.count);
        const value = entry.value != null ? String(entry.value) : "";
        const bucketType = entry.bucketType || getBucketType(value);

        return {
            ...entry,
            value,
            count,
            bucketType,
            percentage: roundPercent(count, total),
            distributionKey: `${bucketType}:${value}:${count}:${index}`,
        };
    });
}

function appendNullBlankBuckets(entries, stats) {
    const total = toNumber(stats && stats.totalRecords);
    const result = [...entries];
    const blankCount = toNumber(stats && stats.blankCount);
    const nullCount = toNumber(stats && stats.nullCount);

    if (blankCount > 0 && !hasBucket(result, "blank")) {
        result.push({
            value: "(blank)",
            count: blankCount,
            bucketType: "blank",
            percentage: roundPercent(blankCount, total),
            distributionKey: `blank:(blank):${blankCount}:exact`,
        });
    }

    if (nullCount > 0 && !hasBucket(result, "null")) {
        result.push({
            value: "(null)",
            count: nullCount,
            bucketType: "null",
            percentage: roundPercent(nullCount, total),
            distributionKey: `null:(null):${nullCount}:exact`,
        });
    }

    return result;
}

function createOthersBucket(stats, displayedValueEntries, label = "Others") {
    const totalRecords = toNumber(stats && stats.totalRecords);
    const totalNonNull = Math.max(0, totalRecords - toNumber(stats && stats.nullCount) - toNumber(stats && stats.blankCount));
    const displayedCount = displayedValueEntries.reduce((sum, entry) => sum + toNumber(entry.count), 0);
    const count = Math.max(0, totalNonNull - displayedCount);

    if (count <= 0) return null;

    const approximate = Boolean(stats && stats.topValuesApproximate) || displayedValueEntries.some((entry) => toNumber(entry.errorBound) > 0);
    const value = approximate ? `${label} (approx.)` : label;

    return {
        value,
        count,
        bucketType: "others",
        approximate,
        percentage: roundPercent(count, totalRecords),
        distributionKey: `others:${value}:${count}`,
    };
}

function buildDisplayDistribution(stats, sourceEntries, limit, options = {}) {
    const normalized = normalizeDistributionEntries(stats, sourceEntries);
    const existingOthers = normalized.find((entry) => entry.bucketType === "others");
    const valueEntries = normalized.filter(isValueBucket).slice(0, limit);
    const entries = [...valueEntries];

    if (existingOthers) {
        entries.push(existingOthers);
    } else if (options.deriveOthers) {
        const othersBucket = createOthersBucket(stats, valueEntries);
        if (othersBucket) entries.push(othersBucket);
    }

    return appendNullBlankBuckets(entries, stats);
}

function buildDistributions(stats, { compactEntries, expandedEntries } = {}) {
    const hasExpandedSource = Array.isArray(expandedEntries) && expandedEntries.length > 0;
    const compactSource = compactEntries || expandedEntries || [];
    const expandedSource = hasExpandedSource ? expandedEntries : compactSource;
    const compactHasLegacyOthers = normalizeDistributionEntries(stats, compactSource).some((entry) => entry.bucketType === "others");
    const compactDistribution = buildDisplayDistribution(stats, compactSource, COMPACT_TOP_VALUE_LIMIT, {
        deriveOthers: !compactHasLegacyOthers,
    });
    const expandedDistribution = hasExpandedSource
        ? buildDisplayDistribution(stats, expandedSource, EXPANDED_TOP_VALUE_LIMIT, { deriveOthers: true })
        : compactDistribution;

    const compactValueCount = compactDistribution.filter(isValueBucket).length;
    const expandedValueCount = expandedDistribution.filter(isValueBucket).length;
    const canViewAll = hasExpandedSource && expandedValueCount > compactValueCount;
    const hasTruncatedDistribution = compactDistribution.some((entry) => entry.bucketType === "others") || canViewAll;
    const distributionApproximate = Boolean(stats && stats.topValuesApproximate)
        || compactDistribution.concat(expandedDistribution).some((entry) => toNumber(entry.errorBound) > 0 || entry.approximate);

    return {
        valueDistribution: compactDistribution,
        expandedValueDistribution: expandedDistribution,
        hasTruncatedDistribution,
        canViewAll,
        distributionApproximate,
    };
}

export {
    COMPACT_TOP_VALUE_LIMIT,
    EXPANDED_TOP_VALUE_LIMIT,
    buildDistributions,
    normalizeDistributionEntries,
};
